import React, { useState } from "react";
import AuthInput from "./AuthInput";
import AuthButton from "./AuthButton";
import PintoAuthShell, {
  CARD_SUBTLE,
  CARD_TEXT,
  PINTO_INPUT_CLASS,
  PINTO_LINK_CLASS,
  PRIMARY_BTN_CLASS,
} from "./PintoAuthShell";
import { ArrowLeft, CheckCircle2, Eye, EyeOff } from "lucide-react";
import useEnforceLightMode from "../hooks/useEnforceLightMode";
import {
  requestChangePassword,
  requestChangeConfirm,
} from "../services/AuthService";

type Step = "request" | "reset" | "success";

interface Props {
  initialStep?: Step;
  initialEmail?: string;
  // Signed-in "Change password" from Settings: same code-by-email flow, but
  // the email is locked to the account and every exit leads back to Settings.
  signedIn?: boolean;
}

const titleClass = `text-[26px] sm:text-3xl font-extrabold tracking-[-0.02em] leading-tight ${CARD_TEXT}`;
const subtitleClass = `text-sm font-medium leading-relaxed ${CARD_SUBTLE}`;
const labelClass = `block text-[13px] font-bold ${CARD_TEXT}`;
const inlineBackClass =
  "inline-flex items-center gap-1.5 text-xs font-bold text-[#7c3aed] hover:text-[#6d28d9] transition-colors";

function ErrorBox({ message }: { message: string }) {
  return (
    <div className="px-4 py-3 rounded-2xl text-[13px] font-medium flex items-start gap-2 animate-fade-in bg-red-50 border border-red-200/70 text-red-700">
      <div className="mt-0.5 shrink-0">
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
            clipRule="evenodd"
          />
        </svg>
      </div>
      {message}
    </div>
  );
}

interface PasswordFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
  show: boolean;
  onToggle: () => void;
}

function PasswordField({
  id,
  label,
  value,
  onChange,
  placeholder,
  show,
  onToggle,
}: PasswordFieldProps) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className={labelClass}>
        {label}
      </label>
      <div className="relative">
        <AuthInput
          id={id}
          type={show ? "text" : "password"}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={`${PINTO_INPUT_CLASS} !pr-11`}
          required
          minLength={8}
        />
        <button
          type="button"
          onClick={onToggle}
          tabIndex={-1}
          aria-label={show ? "Hide password" : "Show password"}
          className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-md transition-colors focus:outline-none text-[#6b5b95] hover:text-[#7c3aed]"
        >
          {show ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
        </button>
      </div>
    </div>
  );
}

const supportFooter = (
  <p className="text-[12px] text-white/85 font-semibold drop-shadow-[0_1px_4px_rgba(30,27,58,0.4)]">
    Need help?{" "}
    <a
      href="/support"
      className="font-extrabold text-white hover:text-amber-200 transition-colors"
    >
      Contact Support
    </a>
  </p>
);

export default function PasswordResetFlow({
  initialStep = "request",
  initialEmail = "",
  signedIn = false,
}: Props) {
  useEnforceLightMode();
  const [step, setStep] = useState<Step>(initialStep);
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [resent, setResent] = useState(false);

  const handleRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!email.trim()) {
      setError("Please enter your email address");
      return;
    }
    setIsLoading(true);
    try {
      await requestChangePassword(email);
      setStep("reset");
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : "Failed to send reset email"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!email.trim()) {
      setError("Please enter your email address");
      return;
    }
    if (!code.trim()) {
      setError("Please enter the reset code");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }
    setIsLoading(true);
    try {
      await requestChangeConfirm(email, code, password);
      setStep("success");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to reset password");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setError("");
    setResent(false);
    try {
      await requestChangePassword(email);
      setResent(true);
      setTimeout(() => setResent(false), 3000);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to resend code");
    }
  };

  // ============================ SUCCESS ============================
  if (step === "success") {
    return (
      <PintoAuthShell>
        <div className="space-y-5 text-center">
          <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-2xl bg-[rgba(124,58,237,0.1)] border border-[rgba(124,58,237,0.18)]">
            <CheckCircle2
              className="w-7 h-7 text-[#7c3aed]"
              strokeWidth={2.4}
            />
          </div>
          <div className="space-y-1.5">
            <h1 className={titleClass}>
              {signedIn ? "Password Updated!" : "Password Reset!"}
            </h1>
            <p className={subtitleClass}>
              {signedIn
                ? "Use your new password the next time you sign in."
                : "You can now sign in with your new password."}
            </p>
          </div>
          <AuthButton
            onClick={() =>
              (window.location.href = signedIn ? "/settings" : "/get-started")
            }
            className={PRIMARY_BTN_CLASS}
          >
            {signedIn ? "Back to Settings" : "Sign In"}
          </AuthButton>
        </div>
      </PintoAuthShell>
    );
  }

  // ============================ REQUEST ============================
  if (step === "request") {
    return (
      <PintoAuthShell footer={supportFooter}>
        <div className="space-y-6">
          <div className="space-y-2">
            <a
              href={signedIn ? "/settings" : "/get-started"}
              className={inlineBackClass}
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              {signedIn ? "Back to settings" : "Back to sign in"}
            </a>
            <h1 className={titleClass}>
              {signedIn ? "Change Password" : "Forgot Password?"}
            </h1>
            <p className={subtitleClass}>
              {signedIn
                ? "We'll email you a code to confirm it's you, then you can pick a new password."
                : "Enter your email and we'll send you a reset code."}
            </p>
          </div>

          {error && <ErrorBox message={error} />}

          <form className="space-y-4" onSubmit={handleRequest}>
            <div className="space-y-1.5">
              <label htmlFor="email" className={labelClass}>
                Email Address
              </label>
              <AuthInput
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
                className={PINTO_INPUT_CLASS}
                readOnly={signedIn && !!initialEmail}
                required
              />
            </div>

            <AuthButton
              type="submit"
              disabled={isLoading}
              className={PRIMARY_BTN_CLASS}
            >
              {isLoading ? "Sending..." : "Send Reset Code"}
            </AuthButton>
          </form>
        </div>
      </PintoAuthShell>
    );
  }

  // ============================ RESET ============================
  return (
    <PintoAuthShell footer={supportFooter}>
      <div className="space-y-6">
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => {
              setStep("request");
              setCode("");
              setPassword("");
              setConfirmPassword("");
              setError("");
              setResent(false);
            }}
            className={inlineBackClass}
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            {signedIn ? "Back" : "Different email"}
          </button>
          <h1 className={titleClass}>Check Your Email</h1>
          <p className={subtitleClass}>
            We sent a code to <strong className={CARD_TEXT}>{email}</strong>.
            Enter it below to set a new password.
          </p>
        </div>

        {error && <ErrorBox message={error} />}

        <form className="space-y-4" onSubmit={handleReset}>
          {/* Reset code — large centered input that looks like a code field */}
          <div className="space-y-1.5">
            <label htmlFor="code" className={labelClass}>
              Reset Code
            </label>
            <AuthInput
              id="code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              className={`${PINTO_INPUT_CLASS} !text-center !text-2xl !tracking-[0.4em] !font-extrabold !py-4`}
              required
            />
          </div>

          <PasswordField
            id="password"
            label="New Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your new password"
            show={showPassword}
            onToggle={() => setShowPassword((s) => !s)}
          />
          <PasswordField
            id="confirmPassword"
            label="Confirm New Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm your new password"
            show={showConfirmPassword}
            onToggle={() => setShowConfirmPassword((s) => !s)}
          />

          <AuthButton
            type="submit"
            disabled={isLoading}
            className={PRIMARY_BTN_CLASS}
          >
            {isLoading ? "Resetting..." : "Reset Password"}
          </AuthButton>
        </form>

        <p className={`text-center text-[13px] font-medium ${CARD_SUBTLE}`}>
          {resent ? (
            <span className="font-bold text-[#7c3aed]">Code resent ✓</span>
          ) : (
            <>
              Didn't receive a code?{" "}
              <button
                type="button"
                onClick={handleResend}
                className={PINTO_LINK_CLASS}
              >
                Resend code
              </button>
            </>
          )}
        </p>
      </div>
    </PintoAuthShell>
  );
}
